<template>
  <div
    class="br-kpi-grid"
    :class="gridClass"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <template v-if="loading">
      <v-card v-for="n in skeletonCount" :key="n" class="br-kpi-card" flat>
        <v-card-text class="pa-2">
          <v-skeleton-loader type="list-item-avatar-two-line" />
        </v-card-text>
      </v-card>
    </template>
    <template v-else>
      <v-card
        v-for="(item, index) in items"
        :key="item.key || index"
        class="br-kpi-card"
        :class="{
          'br-kpi-card--clickable': clickable,
          'br-kpi-card--active': clickable && activeKey === item.key,
        }"
        flat
        @click="clickable && $emit('select', item)"
      >
        <v-card-text class="d-flex align-center pa-3">
          <div
            class="br-kpi-icon mr-3"
            :class="item.tone ? `br-kpi-icon--${item.tone}` : null"
          >
            <v-icon :color="item.iconColor || 'primary'" size="18">{{
              item.icon
            }}</v-icon>
          </div>
          <div class="br-kpi-body">
            <div class="br-kpi-label">
              {{ item.label || $t(item.title) }}
            </div>
            <div
              class="br-kpi-value"
              :class="{
                'br-kpi-value--dark': $vuetify.theme.dark,
                'text-truncate': item.truncate,
              }"
            >
              <v-progress-linear
                v-if="item.loading"
                indeterminate
                height="2"
                class="my-2"
              />
              <slot v-else name="value" :item="item">
                <template v-if="typeof item.value === 'number'">{{
                  item.value | NumberFormat
                }}</template>
                <template v-else>{{ item.value }}</template>
                <span v-if="item.unit" class="text-caption grey--text">
                  {{ item.unit }}</span
                >
              </slot>
            </div>
            <div v-if="item.note" class="br-kpi-note">{{ item.note }}</div>
          </div>
          <slot name="extra" :item="item" :index="index" />
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
// Standard KPI summary row. Visual style lives in the global `.br-kpi-*` rules
// (styles/possmart-brutal.css) — don't re-declare them in pages.
//
// item: { key?, title (i18n key) | label (literal), icon, iconColor?, tone?,
//         value (number is NumberFormat-ed), unit?, note?, loading?, truncate? }
// opt-in filtering: `clickable` + `active-key` highlights the item whose key matches and
//         emits `select` (item) on click.
// slots:  #value="{ item }" replaces the value text, #extra="{ item, index }" adds
//         trailing content (e.g. a currency breakdown) after the label/value block.
import { myMixin } from "@/mixin/main";

export default {
  name: "KpiCards",
  // NumberFormat lives in myMixin (not a global filter); reuse it so numbers match page-wide formatting.
  filters: { NumberFormat: myMixin.filters.NumberFormat },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    // placeholder cards shown while loading (defaults to items.length, min 3)
    skeleton: { type: Number, default: 0 },
    clickable: { type: Boolean, default: false },
    activeKey: { type: [String, Number], default: null },
  },
  computed: {
    skeletonCount() {
      return this.skeleton || Math.max(this.items.length, 3);
    },
    gridClass() {
      const n = this.loading ? this.skeletonCount : this.items.length;
      return n === 3 ? "br-kpi-grid--3" : n === 4 ? "br-kpi-grid--4" : null;
    },
  },
};
</script>
